import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, pollJob, rateLimitMessage } from "@/lib/api";
import { Loader, LoaderStages } from "@/components/Loader";
import { RegionBadge, REGION_KEYS } from "@/components/RegionBadge";
import { ArrowLeft, Sparkles, Target, ArrowRight, Trophy } from "lucide-react";

const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
    return `$${Number(n).toFixed(0)}`;
};

const tierTone = (score) => {
    if (score >= 90) return "text-emerald-400 border-emerald-500/40 bg-emerald-500/10";
    if (score >= 80) return "text-blue-400 border-blue-500/40 bg-blue-500/10";
    if (score >= 70) return "text-yellow-400 border-yellow-500/40 bg-yellow-500/10";
    return "text-zinc-400 border-zinc-700 bg-zinc-900/40";
};

export default function Partners() {
    const { id: firmId } = useParams();
    const [firm, setFirm] = useState(null);
    const [industries, setIndustries] = useState(["All"]);
    const [industry, setIndustry] = useState("All");
    const [region, setRegion] = useState("All");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.get(`/firms/${firmId}`).then(({ data }) => setFirm(data));
        api.get("/firms").then(({ data }) => {
            const set = new Set(["All"]);
            (data || []).filter((f) => f.id !== firmId).forEach((f) => f.industry && set.add(f.industry));
            setIndustries([...set]);
        });
    }, [firmId]);

    const run = async () => {
        setLoading(true); setError(""); setResult(null);
        try {
            const { data: enqueued } = await api.post(`/firms/${firmId}/find-partners`, { industry, region, limit: 10 });
            const r = await pollJob(enqueued.job_id, { timeoutMs: 5 * 60 * 1000 });
            setResult(r);
        } catch (e) {
            const rl = rateLimitMessage(e);
            const d = e?.response?.data?.detail;
            setError(rl || (typeof d === "string" ? d : (e.message || "Matchmaker failed")));
        } finally {
            setLoading(false);
        }
    };

    if (!firm) return <div className="p-12"><Loader label="Loading firm" /></div>;

    return (
        <div className="p-8" data-testid="partners-page">
            <div className="mb-6 flex items-center justify-between">
                <Link to={`/app/firms/${firmId}`} className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-amber-500" data-testid="back-to-firm">
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to {firm.name}
                </Link>
            </div>

            <header className="mb-6 border-b border-zinc-800 pb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Matchmaker</div>
                <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">Find partners for {firm.name}</h1>
                <p className="text-sm text-zinc-500 mt-2">Scores every firm in your library against {firm.name} and ranks the best 10 fits in one shot.</p>
            </header>

            <div className="border border-zinc-800 bg-zinc-950 p-5 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">Industry filter</label>
                        <select
                            data-testid="industry-filter"
                            value={industry}
                            onChange={(e) => setIndustry(e.target.value)}
                            className="w-full bg-zinc-950 border border-zinc-800 px-3 py-3 text-sm text-zinc-100 focus:outline-none focus:border-amber-500 font-mono"
                        >
                            {industries.map((i) => <option key={i} value={i}>{i}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">Region filter</label>
                        <select
                            data-testid="region-filter"
                            value={region}
                            onChange={(e) => setRegion(e.target.value)}
                            className="w-full bg-zinc-950 border border-zinc-800 px-3 py-3 text-sm text-zinc-100 focus:outline-none focus:border-amber-500 font-mono"
                        >
                            <option value="All">All</option>
                            {REGION_KEYS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={run}
                            disabled={loading}
                            data-testid="run-matchmaker-btn"
                            className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-semibold text-xs uppercase tracking-wider px-5 py-3 flex items-center justify-center gap-2"
                        >
                            <Sparkles className="w-4 h-4" /> {loading ? "Scoring…" : "Find partners"}
                        </button>
                    </div>
                </div>
            </div>

            {error && <div data-testid="matchmaker-error" className="border border-red-900/60 bg-red-950/30 text-red-400 text-sm px-4 py-3 font-mono mb-6">{error}</div>}

            {loading && (
                <div className="border border-zinc-800 bg-zinc-950 p-12">
                    <Loader label="Scoring your library" stages={LoaderStages.partners} />
                </div>
            )}

            {result && !loading && (
                <div className="border border-zinc-800 bg-zinc-950" data-testid="matchmaker-result">
                    <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-amber-500" />
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                            Top {result.matches.length} fit{result.matches.length === 1 ? "" : "s"} from {result.candidate_count} candidate{result.candidate_count === 1 ? "" : "s"}
                        </div>
                    </div>
                    {result.matches.length === 0 ? (
                        <div className="p-12 text-center">
                            <Target className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                            <div className="font-heading font-bold text-zinc-300 text-lg mb-2">No candidates match those filters</div>
                            <div className="text-sm text-zinc-500">Loosen industry / region or add more firms to your library.</div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="matchmaker-table">
                                <thead>
                                    <tr className="border-b border-zinc-800 text-left">
                                        <th className="px-5 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500 w-12">#</th>
                                        <th className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Target firm</th>
                                        <th className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Best product</th>
                                        <th className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Why</th>
                                        <th className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500 text-right">Fit</th>
                                        <th className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500 text-right">Projected</th>
                                        <th className="px-5 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500 text-right"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.matches.map((m, i) => {
                                        const key = `${firmId}:${m.firm_b_id}:${m.best_product_id}`;
                                        return (
                                            <tr key={m.firm_b_id} className="border-b border-zinc-800/60 hover:bg-zinc-900/40 align-top" data-testid={`match-row-${i}`}>
                                                <td className="px-5 py-3 font-mono text-xs text-zinc-600">{String(i + 1).padStart(2, "0")}</td>
                                                <td className="px-3 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <Link to={`/app/firms/${m.firm_b_id}`} className="text-zinc-50 hover:text-amber-400 font-semibold">{m.firm_b_name}</Link>
                                                        {m.firm_b_region && <RegionBadge region={m.firm_b_region} />}
                                                    </div>
                                                    <div className="text-xs text-zinc-500 font-mono mt-0.5">{m.firm_b_industry || "—"}</div>
                                                </td>
                                                <td className="px-3 py-3 text-amber-500 font-mono text-xs">{m.best_product_name || "—"}</td>
                                                <td className="px-3 py-3 text-zinc-300 text-xs leading-relaxed max-w-md">{m.rationale}</td>
                                                <td className="px-3 py-3 text-right">
                                                    <span className={`inline-block border px-2 py-0.5 font-mono ${tierTone(m.fit_score)}`} data-testid={`match-fit-${i}`}>
                                                        {m.fit_score}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-3 text-right font-mono text-amber-500 text-xs">{fmtMoney(m.estimated_added_annual_revenue_usd)}</td>
                                                <td className="px-5 py-3 text-right">
                                                    <Link
                                                        to={`/app/synergy?key=${encodeURIComponent(key)}`}
                                                        data-testid={`match-deep-dive-${i}`}
                                                        className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-amber-500 hover:text-amber-300"
                                                    >
                                                        Deep dive <ArrowRight className="w-3 h-3" />
                                                    </Link>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {!result && !loading && !error && (
                <div className="border border-dashed border-zinc-800 p-12 text-center">
                    <Target className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest">Click "Find partners" to score your library</div>
                </div>
            )}
        </div>
    );
}
